import type {
  DesktopPraxaNotificationsBridge,
  PraxaDesktopNotificationActionResult,
  PraxaDesktopNotificationCenterState,
} from "@praxa/platform-contracts";
import { useCallback, useEffect, useRef, useState } from "react";

export const PRAXA_NOTIFICATIONS_LOAD_ERROR =
  "Praxa notifications couldn’t load. Local coding is still available.";

function getBridge(): DesktopPraxaNotificationsBridge | undefined {
  return typeof window === "undefined" ? undefined : window.desktopBridge?.praxaNotifications;
}

export function useDesktopPraxaNotifications(): {
  readonly bridge: DesktopPraxaNotificationsBridge | undefined;
  readonly error: string | null;
  readonly loading: boolean;
  readonly markAllRead: () => Promise<PraxaDesktopNotificationActionResult | null>;
  readonly markRead: (
    notificationIds: readonly string[],
  ) => Promise<PraxaDesktopNotificationActionResult | null>;
  readonly refresh: () => Promise<void>;
  readonly revision: number;
  readonly state: PraxaDesktopNotificationCenterState | null;
} {
  const bridge = getBridge();
  const [state, setState] = useState<PraxaDesktopNotificationCenterState | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [revision, setRevision] = useState(0);
  const eventVersionRef = useRef(0);

  const applyState = useCallback((nextState: PraxaDesktopNotificationCenterState) => {
    setState(nextState);
    setError(null);
    setRevision((current) => current + 1);
  }, []);

  const refresh = useCallback(async () => {
    if (!bridge) return;
    const eventVersion = eventVersionRef.current;
    setLoading(true);
    setError(null);
    try {
      const nextState = await bridge.refresh();
      if (eventVersionRef.current === eventVersion) applyState(nextState);
    } catch {
      if (eventVersionRef.current === eventVersion) setError(PRAXA_NOTIFICATIONS_LOAD_ERROR);
    } finally {
      setLoading(false);
    }
  }, [applyState, bridge]);

  const markRead = useCallback(
    async (notificationIds: readonly string[]) => {
      if (!bridge) return null;
      const result = await bridge.markRead(notificationIds);
      applyState(result.state);
      return result;
    },
    [applyState, bridge],
  );

  const markAllRead = useCallback(async () => {
    if (!bridge) return null;
    const result = await bridge.markAllRead();
    applyState(result.state);
    return result;
  }, [applyState, bridge]);

  useEffect(() => {
    if (!bridge) {
      setState(null);
      setError(null);
      setLoading(false);
      return;
    }
    let active = true;
    let unsubscribe = () => {};
    try {
      unsubscribe = bridge.onStateChange((nextState) => {
        eventVersionRef.current += 1;
        if (active) applyState(nextState);
      });
    } catch {
      setError(PRAXA_NOTIFICATIONS_LOAD_ERROR);
    }
    const eventVersion = eventVersionRef.current;
    setLoading(true);
    void bridge
      .getState()
      .then((nextState) => {
        if (active && eventVersionRef.current === eventVersion) applyState(nextState);
      })
      .catch(() => {
        if (active && eventVersionRef.current === eventVersion) {
          setError(PRAXA_NOTIFICATIONS_LOAD_ERROR);
        }
      })
      .finally(() => {
        if (active) setLoading(false);
      });
    return () => {
      active = false;
      unsubscribe();
    };
  }, [applyState, bridge]);

  return {
    bridge,
    error,
    loading,
    markAllRead,
    markRead,
    refresh,
    revision,
    state,
  };
}
